import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    company: {
      name: 'We OTP',
      logo: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      copyright: ''
    },
    sms: {
      provider: 'fast2sms',
      apiKey: '',
      senderId: '',
      enabled: false
    },
    firebase: {
      projectId: '',
      clientEmail: '',
      privateKeyId: '',
      clientId: '',
      privateKey: '',
      authUri: 'https://accounts.google.com/o/oauth2/auth',
      tokenUri: 'https://oauth2.googleapis.com/token',
      authProviderX509CertUrl: 'https://www.googleapis.com/oauth2/v1/certs',
      clientX509CertUrl: '',
      enabled: false
    },
    socialLogin: {
      google: {
        enabled: false,
        clientIdWeb: '',
        clientIdIos: '',
        clientIdAndroid: '',
        clientSecret: ''
      },
      apple: {
        enabled: false
      }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = async (isPublic = false) => {
    try {
      setLoading(true);
      let response;
      
      if (isPublic) {
        // Try public endpoint first (for login page)
        try {
          response = await axios.get(`${API_BASE_URL}/public/settings/company`);
          const data = response.data.data;
          setSettings({
            company: {
              name: data.name || 'We OTP',
              logo: data.logo || '',
              email: data.email || '',
              phone: data.phone || '',
              website: data.website || '',
              address: data.address || '',
              copyright: data.copyright || ''
            },
            sms: {
              provider: 'fast2sms',
              apiKey: '',
              senderId: '',
              enabled: false
            },
            firebase: {
              projectId: '',
              clientEmail: '',
              privateKeyId: '',
              clientId: '',
              privateKey: '',
              authUri: 'https://accounts.google.com/o/oauth2/auth',
              tokenUri: 'https://oauth2.googleapis.com/token',
              authProviderX509CertUrl: 'https://www.googleapis.com/oauth2/v1/certs',
              clientX509CertUrl: '',
              enabled: false
            }
          });
          setError(null);
          return;
        } catch (publicError) {
          // If public endpoint fails, try admin endpoint (if authenticated)
          // This allows authenticated users to get full settings
          try {
            response = await axios.get(`${API_BASE_URL}/admin/settings`);
            const data = response.data.data;
            setSettings({
              company: {
                name: data.company?.name || 'We OTP',
                logo: data.company?.logo || '',
                email: data.company?.email || '',
                phone: data.company?.phone || '',
                website: data.company?.website || '',
                address: data.company?.address || '',
                copyright: data.company?.copyright || ''
              },
              sms: {
                provider: data.sms?.provider || 'fast2sms',
                apiKey: data.sms?.apiKey || '',
                senderId: data.sms?.senderId || '',
                enabled: data.sms?.enabled || false
              },
              firebase: {
                projectId: data.firebase?.projectId || '',
                clientEmail: data.firebase?.clientEmail || '',
                privateKeyId: data.firebase?.privateKeyId || '',
                clientId: data.firebase?.clientId || '',
                privateKey: data.firebase?.privateKey || '',
                authUri: data.firebase?.authUri || 'https://accounts.google.com/o/oauth2/auth',
                tokenUri: data.firebase?.tokenUri || 'https://oauth2.googleapis.com/token',
                authProviderX509CertUrl: data.firebase?.authProviderX509CertUrl || 'https://www.googleapis.com/oauth2/v1/certs',
                clientX509CertUrl: data.firebase?.clientX509CertUrl || '',
                enabled: data.firebase?.enabled || false
              },
              socialLogin: {
                google: {
                  enabled: data.socialLogin?.google?.enabled || false,
                  clientIdWeb: data.socialLogin?.google?.clientIdWeb || '',
                  clientIdIos: data.socialLogin?.google?.clientIdIos || '',
                  clientIdAndroid: data.socialLogin?.google?.clientIdAndroid || '',
                  clientSecret: data.socialLogin?.google?.clientSecret || ''
                },
                apple: {
                  enabled: data.socialLogin?.apple?.enabled || false
                }
              }
            });
            setError(null);
            return;
          } catch (adminError) {
            // Both failed, use defaults
            throw publicError;
          }
        }
      } else {
        // Admin endpoint (requires auth)
        response = await axios.get(`${API_BASE_URL}/admin/settings`);
        const data = response.data.data;
        setSettings({
          company: {
            name: data.company?.name || 'We OTP',
            logo: data.company?.logo || '',
            email: data.company?.email || '',
            phone: data.company?.phone || '',
            website: data.company?.website || '',
            address: data.company?.address || '',
            copyright: data.company?.copyright || ''
          },
          sms: {
            provider: data.sms?.provider || 'fast2sms',
            apiKey: data.sms?.apiKey || '',
            senderId: data.sms?.senderId || '',
            enabled: data.sms?.enabled || false
          },
          firebase: {
            projectId: data.firebase?.projectId || '',
            clientEmail: data.firebase?.clientEmail || '',
            privateKeyId: data.firebase?.privateKeyId || '',
            clientId: data.firebase?.clientId || '',
            privateKey: data.firebase?.privateKey || '',
            authUri: data.firebase?.authUri || 'https://accounts.google.com/o/oauth2/auth',
            tokenUri: data.firebase?.tokenUri || 'https://oauth2.googleapis.com/token',
            authProviderX509CertUrl: data.firebase?.authProviderX509CertUrl || 'https://www.googleapis.com/oauth2/v1/certs',
            clientX509CertUrl: data.firebase?.clientX509CertUrl || '',
            enabled: data.firebase?.enabled || false
          },
          socialLogin: {
            google: {
              enabled: data.socialLogin?.google?.enabled || false,
              clientIdWeb: data.socialLogin?.google?.clientIdWeb || '',
              clientIdIos: data.socialLogin?.google?.clientIdIos || '',
              clientIdAndroid: data.socialLogin?.google?.clientIdAndroid || '',
              clientSecret: data.socialLogin?.google?.clientSecret || ''
            },
            apple: {
              enabled: data.socialLogin?.apple?.enabled || false
            }
          }
        });
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Only set error if we're not just falling back to defaults
      if (!isPublic || error.response?.status !== 401) {
        setError('Failed to load settings');
      }
      // Keep default settings on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Try to fetch settings - will use public endpoint if not authenticated
    fetchSettings(true);
  }, []);

  const refreshSettings = (isPublic = false) => {
    fetchSettings(isPublic);
  };

  const getLogoUrl = () => {
    if (settings.company.logo) {
      return `${API_BASE_URL_WITHOUT_API}${settings.company.logo}`;
    }
    return null;
  };

  const value = {
    settings,
    loading,
    error,
    refreshSettings,
    getLogoUrl,
    companyName: settings.company.name,
    companyLogo: settings.company.logo,
    logoUrl: getLogoUrl()
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsContext;

